import styles from './styles.module.scss';
import { useEffect, useMemo, useState } from 'react';
import Typography from '@components/common/typography';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiGiftCardsService } from '@api';
import { ENewNotificationType } from '@enum';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import {
    EGiftCardStatus,
    EUserType,
    TGetGiftCardsFilter,
    TGiftCardListItem
} from '@xeppt/xeppt-sdk/types';
import { useDebounceValue } from 'usehooks-ts';
import CardsTable from './cards_table';
import { Icon } from '@components/icons';
import Info from '@components/common/info';
import { prettifyAmount } from '@utils/index';
import { useUserContext } from '@hooks/context/useUserContext';
import Button from '@components/common/button';
import { useGiftCardsModals } from '@hooks/modals/useGiftCardsModals';
import { useQueryParams } from '@hooks/helpers/useQueryParams';

const GiftCardsSection = () => {
    const [redeemId] = useQueryParams(['redeem_id']);
    const [giftCards, setGiftCards] = useState<TGiftCardListItem[]>([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useDebounceValue(search, 500);
    const [selectedCards, setSelectedCards] = useState<string[]>([]);
    const [filter, setFilter] = useState<TGetGiftCardsFilter>({
        page: 1,
        page_size: 15,
        status: [],
        order: 'DESC',
        order_field: 'gift_card_serial_number'
    });
    const { user } = useUserContext();
    const { handleCancelGiftCards } = useGiftCardsModals({
        refetchGiftCards: () => refetchGiftCards({ isNew: true })
    });

    const {
        data: giftCardsData,
        isLoading,
        handleRequest: refetchGiftCards
    } = useApiQuery({
        isInitialRequest: false,
        method: (request?: { isNew?: boolean; filter?: TGetGiftCardsFilter }) => {
            const opt = request?.filter || filter;
            return apiGiftCardsService.getGiftCards({
                ...opt,
                page: request?.isNew ? 1 : opt.page,
                search
            });
        },
        onSuccess: (data) => {
            if (data.page !== 1) {
                setGiftCards((state) => [...state, ...data.data]);
            } else {
                setGiftCards(data?.data || []);
            }
        },
        notificationType: ENewNotificationType.ACCOUNT,
        condition: user && user?.type === EUserType.BUSINESS
    });
    const { handleOpenActivateGiftCardModal, handleOpenRedeemGiftCardModal } = useGiftCardsModals({
        refetchGiftCards: () => refetchGiftCards({ isNew: true })
    });

    useEffect(() => {
        if (redeemId) {
            handleOpenRedeemGiftCardModal(redeemId.replaceAll(' ', '+'));
        }
    }, [redeemId]);

    useEffect(() => {
        setDebouncedSearch(search);
    }, [search]);

    useEffect(() => {
        if (debouncedSearch.length >= 3) {
            refetchGiftCards({ isNew: true, filter });
        } else if (debouncedSearch.length === 0) {
            refetchGiftCards({ isNew: true, filter });
        }
    }, [debouncedSearch]);

    const canBeActivated = useMemo(() => {
        const selectedCardsArray = giftCards.filter((card) => selectedCards.includes(card.id));
        return selectedCardsArray.every((card) => {
            return (
                (!!card.email || (!!card.phone_number && !!card.phone_code)) &&
                card.status === EGiftCardStatus.ISSUED
            );
        });
    }, [giftCards, selectedCards]);

    const canBeCancelled = useMemo(() => {
        const selectedCardsArray = giftCards.filter((card) => selectedCards.includes(card.id));
        return (
            selectedCardsArray.every((card) => {
                return card.status === EGiftCardStatus.ISSUED;
            }) ||
            selectedCardsArray.every(
                (card) =>
                    card.status !== EGiftCardStatus.REDEEMED &&
                    card.status !== EGiftCardStatus.CANCELLED &&
                    card.status !== EGiftCardStatus.CANCEL_REQUESTED
            )
        );
    }, [giftCards, selectedCards]);

    const onCancelGiftCards = () => {
        const cardsToCancel = giftCards.filter((card) => selectedCards.includes(card.id));
        handleCancelGiftCards(cardsToCancel);
    };

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    <div className={styles.header}>
                        <Typography variant="h4">Gift cards</Typography>
                    </div>
                </div>
                <div className={styles.points_wrapper}>
                    <div className={`${styles.point_wrapper} ${styles.pending}`}>
                        <div className={styles.icon}>
                            <Icon name="watches" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <div className={styles.title}>
                                <Typography variant="body3">Reserved balance</Typography>
                                <Info
                                    variant="bottom"
                                    content="The total amount currently reserved for your active gift cards and not yet redeemed"
                                />
                            </div>
                            <Typography variant="h3">
                                {prettifyAmount(
                                    giftCardsData?.total_reserved_balance?.toFixed(2) || '0.00'
                                )}{' '}
                                CAD
                            </Typography>
                        </div>
                    </div>
                    <div className={`${styles.point_wrapper} ${styles.green}`}>
                        <div className={styles.icon}>
                            <Icon name="watches" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <div className={styles.title}>
                                <Typography variant="body3">Total redeemed balance</Typography>
                                <Info
                                    variant="bottom"
                                    content="The total amount that has already been redeemed from your gift cards"
                                />
                            </div>
                            <Typography variant="h3">
                                {prettifyAmount(
                                    giftCardsData?.total_redeemed_balance?.toFixed(2) || '0.00'
                                )}{' '}
                                CAD
                            </Typography>
                        </div>
                    </div>
                </div>
                <CardsTable
                    refetchGiftCards={refetchGiftCards}
                    giftCards={giftCards}
                    onFilterChange={(filter) => {
                        refetchGiftCards({ isNew: filter?.page === 1, filter });
                        setFilter((state) => ({ ...state, ...filter }));
                    }}
                    filter={filter}
                    onSearch={(val) => setSearch(val)}
                    search={search}
                    isLoading={isLoading}
                    selectedCards={selectedCards}
                    giftCardsData={giftCardsData}
                    onChangeSelectedCards={(cards) => setSelectedCards(cards)}
                />
                {selectedCards.length > 0 && (
                    <div className={styles.bulk_actions_wrapper}>
                        <Typography className={styles.selected}>
                            <span>{selectedCards.length}</span> of{' '}
                            <span>{giftCardsData?.total}</span> selected
                        </Typography>
                        <div className={styles.bulk_actions}>
                            {canBeActivated || canBeCancelled ? (
                                <>
                                    <Button
                                        size="small"
                                        leftIcon="send"
                                        variant="text-white"
                                        disabled={!canBeActivated}
                                        onClick={() =>
                                            handleOpenActivateGiftCardModal(
                                                giftCards.filter((card) =>
                                                    selectedCards.includes(card.id)
                                                )
                                            )
                                        }>
                                        Send for activation
                                    </Button>
                                    <Button
                                        size="small"
                                        disabled={!canBeCancelled}
                                        leftIcon="close_outlined"
                                        variant="text-white"
                                        onClick={onCancelGiftCards}>
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Typography variant="body4" className={styles.no_actions}>
                                    No bulk actions available for this selection
                                </Typography>
                            )}
                            <Button
                                size="small"
                                leftIcon="close"
                                variant="text-white"
                                onClick={() => setSelectedCards([])}
                            />
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default GiftCardsSection;
